<template>
    <div class="container">
        <h1 aria-hidden="true">Os Online</h1>

        <transition mode="out-in" name="fade">
            <div class="transition-root" key="reconnecting" v-if="isReconnecting">
                <div class="row">
                    <loading-indicator>Prøver at genforbinde..</loading-indicator>
                </div>
            </div>
            <div class="transition-root" key="startingGame" v-else-if="status === 'startingGame'">
                <div class="row">
                    <loading-indicator>Starter spil..</loading-indicator>
                </div>
            </div>
            <div class="transition-root" key="waitingOnTheme" v-else-if="status === 'waitingOnTheme'">
                <div class="row">
                    <loading-indicator>Venter på at din lærer vælger et case..</loading-indicator>
                </div>
            </div>
            <div class="transition-root" key="waitingOnCase" v-else-if="status === 'waitingOnCase'">
                <div class="row">
                    <loading-indicator>Venter på at din lærer vælger en case..</loading-indicator>
                </div>
            </div>
            <div class="transition-root" key="connecting" v-else-if="status === 'connecting'">
                <div class="row">
                    <loading-indicator>Forbinder..</loading-indicator>
                </div>
            </div>
            <div class="transition-root" key="loading" v-else-if="status === 'loading'">
                <div class="row">
                    <loading-indicator>Henter tema og case..</loading-indicator>
                </div>
            </div>

            <div class="transition-root" key="none" v-else>
                <div class="text-center">Nu skal du taste emoji-koden, fra skærmen i klassen</div>

                <div class="row">
                    <emoji-code-picker
                            v-bind:activeEmojiIndex="activeEmojiIndex"
                            v-model="roomCode"
                            v-on:activeEmojiIndexChange="activeEmojiIndexChange"
                            v-on:fillOutStatusChanged="onFilloutStatusChanged"
                    />
                </div>

                <div class="continue-button-container">
                    <app-button :enabled="canStart" ref="submitButton" v-on:click.native="onClickStart">Start
                    </app-button>
                </div>
            </div>
        </transition>
    </div>
</template>

<script>
    import {$eBus} from '@/event-bus'

    import PlayerType from '@/enum/PlayerType'
    import GameClientEvent from '@/enum/GameClientEvent'
    import LoadingIndicator from '@/components/loading-indicator/index'
    import * as MutationTypes from '@/store/mutation-types'
    import EmojiCodePicker from '@/components/emoji-code/picker'
    import AppButton from '@/components/app-button'
    import {CurrentUrlInfo} from '@/current-url-info'
    import AppConfig from '@/app-config'
    import {mapGetters} from 'vuex'
    import {getStudentUrl} from '@/utils/UrlUtil'
    import {focusReference} from '@/utils/UiUtil';

    const STATUS_NONE = 'none';
    const STATUS_CONNECTING = 'connecting';
    const STATUS_WAITING_ON_THEME = 'waitingOnTheme';
    const STATUS_WAITING_ON_CASE = 'waitingOnCase';
    const STATUS_LOADING = 'loading';
    const STATUS_STARTING_GAME = 'startingGame';

    export default {
        components: {
            LoadingIndicator,
            EmojiCodePicker,
            AppButton
        },
        data() {
            let currentUrlInfo = CurrentUrlInfo.instance()

            let apiHost = AppConfig.apiHost || currentUrlInfo.getCurrentHost();
            let apiPort = AppConfig.apiPort || currentUrlInfo.getCurrentPort();
            let apiSecure = AppConfig.apiSecure || currentUrlInfo.getCurrentScheme() === 'https';

            let colyseusHost = AppConfig.colyseusHost || apiHost;
            let colyseusPort = AppConfig.colyseusPort;
            let colyseusSecure = AppConfig.colyseusSecure || false;

            return {
                canStart: false,
                roomCode: '0000',

                activeEmojiIndex: 0,

                apiHost: apiHost,
                apiPort: apiPort,
                apiSecure: apiSecure,

                colyseusHost: colyseusHost,
                colyseusPort: colyseusPort,
                colyseusSecure: colyseusSecure,

                selectedThemeId: null,
                selectedCaseId: null,

                status: STATUS_NONE,

                isReconnecting: false
            }
        },
        computed: {
            ...mapGetters({
                lastEmojiCode: 'getLastEmojiCode',
                currentTheme: 'getCurrentTheme',
                currentCase: 'getCurrentCase',
                gameClientTryReconnect: 'getGameClientTryReconnect',
            }),
        },
        mounted() {
            this.selectedThemeId = null;
            this.selectedCaseId = null;

            this.$store.commit(MutationTypes.SET_CURRENT_THEME, null);
            this.$store.commit(MutationTypes.SET_CURRENT_CASE, null);

            // Check if we're already connected to a room
            if (this.$gameClient && this.$gameClient.room) {

                console.log('We are already in a room, onJoinedRoom', this.$gameClient.room);

                this.onJoinedRoom(this.$gameClient.room);

                return;
            }

            // If we're not already connected to a room, did we previously try connecting to a room with a specific id?
            if (this.roomCode === '0000' && this.lastEmojiCode) {
                this.roomCode = this.lastEmojiCode;
                this.activeEmojiIndex = this.roomCode.length - 1;
                this.canStart = this.activeEmojiIndex === 3;

                if (!this.gameClientTryReconnect) {
                    this.onClickStart();
                }
            }

            focusReference(this, 'introText');

            if (this.gameClientTryReconnect) {
                this.tryReconnect();
            }
        },
        beforeDestroy() {
            $eBus.$off(GameClientEvent.RoomStateChange, this.onRoomStateChange);
        },
        watch: {
            gameClientTryReconnect(gameClientTryReconnect) {
                if (gameClientTryReconnect) {
                    this.tryReconnect();
                }
            },
            lastEmojiCode(lastEmojiCode) {
                console.log('lastEmojiCode', lastEmojiCode);
            },
            canStart(canStart) {
                if (!canStart) {
                    return;
                }

                focusReference(this, 'submitButton', 0);
            },
            selectedThemeId(selectedThemeId) {
                this.updateThemeCase();
            },
            selectedCaseId(selectedCaseId) {
                this.updateThemeCase();
            },
            currentTheme() {
                this.checkReady();
            },
            currentCase() {
                this.checkReady();
            }
        },
        methods: {
            tryReconnect() {
                if (this.isReconnecting) {
                    return; // We are already reconnecting.. calm down!
                }

                this.$store.commit(MutationTypes.SET_GAME_CLIENT_TRY_RECONNECT, false);

                this.isReconnecting = true;

                this.$gameClient.reconnect().then(() => {
                    this.isReconnecting = false;
                }).catch(error => {
                    // It didn't go OK..
                    this.isReconnecting = false;
                });
            },
            activeEmojiIndexChange(activeEmojiIndex) {
                console.log('Active emoji index', activeEmojiIndex)

                this.activeEmojiIndex = activeEmojiIndex
            },
            onFilloutStatusChanged(canStart) {
                this.canStart = canStart
            },
            onClickStart() {
                if (!this.canStart) {
                    return
                }

                this.connect()
            },

            updateThemeCase() {
                if (!this.selectedThemeId) {
                    this.status = STATUS_WAITING_ON_THEME;

                    return;
                }

                if (!this.selectedCaseId) {
                    this.status = STATUS_WAITING_ON_CASE;

                    return;
                }

                this.status = STATUS_LOADING;

                this.$store.commit(MutationTypes.SET_CURRENT_THEME, null)
                this.$store.commit(MutationTypes.SET_CURRENT_CASE, null)

                this.$apiClient.getTheme(this.selectedThemeId, (_theme) => {
                    this.$store.commit(MutationTypes.SET_CURRENT_THEME, _theme)

                    this.$apiClient.getCase(this.selectedThemeId, this.selectedCaseId, (_case) => {
                        this.$store.commit(MutationTypes.SET_CURRENT_CASE, _case)
                    })
                })
            },
            checkReady() {
                if (!this.$gameClient.room) {
                    return false;
                }

                if (!this.currentTheme) {
                    return false;
                }

                if (!this.currentCase) {
                    return false;
                }

                this.status = STATUS_STARTING_GAME;

                this.$store.commit(MutationTypes.SET_CURRENT_PAGE_INDEX, this.$gameClient.room.state.currentPageIndex || 0);

                this.$nextTick(() => {
                    this.$router.push(getStudentUrl('game'))
                });
            },
            onClientError() {
                this.$gameClient.disconnect()

                this.$store.commit(MutationTypes.SET_ERROR_ICON, 'connectionError')
                this.$store.commit(MutationTypes.SET_ERROR_MESSAGE, 'Kunne ikke forbinde')
            },
            onRoomStateChange(room, state) {
                if (state.hasOwnProperty('selectedThemeId') && state.selectedThemeId > 0) {
                    this.selectedThemeId = state.selectedThemeId;
                }

                if (state.hasOwnProperty('selectedCaseId') && state.selectedCaseId > 0) {
                    this.selectedCaseId = state.selectedCaseId;
                }
            },
            connect() {
                this.$gameClient.setUrlFromHostPort(this.colyseusHost, this.colyseusPort, this.colyseusSecure);
                this.$apiClient.setBaseUrlFromHostPort(this.apiHost, this.apiPort, this.apiSecure);

                this.status = STATUS_CONNECTING;

                this.$gameClient.joinRoom(this.roomCode, {
                    playerType: PlayerType.Student,
                }).then(this.onJoinedRoom).catch(err => {
                    console.log('could not connect to server', err);

                    // Could not connect to server
                    this.$store.commit(MutationTypes.SET_ERROR_ICON, 'connectionError')
                    this.$store.commit(MutationTypes.SET_ERROR_MESSAGE, 'Kunne ikke forbinde til serveren')

                    this.status = STATUS_NONE;
                });
            },
            onJoinedRoom(room) {
                this.status = STATUS_WAITING_ON_THEME;

                this.$store.commit(MutationTypes.SET_LAST_EMOJI_CODE, this.roomCode)

                this.onRoomStateChange(room, room.state || {});

                $eBus.$off(GameClientEvent.RoomStateChange, this.onRoomStateChange);
                $eBus.$on(GameClientEvent.RoomStateChange, this.onRoomStateChange);

                this.checkReady();
            }
        }
    }
</script>

<style lang="less" scoped>
    .continue-button-container {
        text-align: center;
        width: 100%;
        margin-top: 30px;
    }
</style>
